"use client";

import { useState, type FormEvent } from "react";

import { Button } from "@/src/components/ui/Button";
import { Heading, Text } from "@/src/components/ui/Typography";
import { FormError } from "@/src/components/ui/FormError";
import { FormSuccess } from "@/src/components/ui/FormSuccess";

type Status = "idle" | "loading" | "success" | "error";

export function SubscribeForm() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("loading");
    setErrorMessage("");

    const formData = new FormData(e.currentTarget);
    const honeypot = (formData.get("website_url") as string | null)?.trim();

    try {
      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, website_url: honeypot }),
      });
      const data = await res.json();

      if (!res.ok) {
        setStatus("error");
        setErrorMessage(data.message || "Something went wrong. Please try again.");
        return;
      }

      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
      setErrorMessage("Something went wrong. Please try again.");
    }
  }

  return (
    <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
      <div>
        <Heading as="h2" size="headingLG" className="text-neutral-700">
          Subscribe to our blog
        </Heading>
        <Text size="bodyMD" className="mt-2 text-neutral-600">
          Once a month we will send you blog updates
        </Text>
      </div>

      <div className="w-full md:max-w-md">
        <form
          onSubmit={handleSubmit}
          className="group relative flex items-end gap-4 border-b border-neutral-300 pb-3"
        >
          <input
            type="text"
            name="website_url"
            tabIndex={-1}
            autoComplete="off"
            className="hidden"
            aria-hidden="true"
          />

          <input
            type="email"
            required
            placeholder="Your Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === "loading"}
            className="flex-1 bg-transparent text-neutral-700 outline-none placeholder:text-neutral-400"
          />

          <Button variant="link-plain" type="submit" disabled={status === "loading"} className="h-fit">
            {status === "loading" ? "Subscribing…" : "Subscribe"}
          </Button>

          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 -bottom-px h-px origin-left scale-x-0 bg-neutral-500 transition-transform duration-400 ease-out group-hover:scale-x-100"
          />
        </form>

        {status === "success" && (
          <FormSuccess className="mt-4">
            Subscribed! Check your inbox for updates.
          </FormSuccess>
        )}
        {status === "error" && (
          <FormError className="mt-4">{errorMessage}</FormError>
        )}
      </div>
    </div>
  );
}
